import GeneralUtility from '../utilities/general-utility';
import * as Joi from 'joi';
import {DateSchema, Reference, Schema} from 'joi';
import DateExtension from '@joi/date';
import moment from 'moment';
import mitt, {Emitter} from 'mitt';

const ExtendedJoi = Joi.extend(DateExtension) as typeof Joi;

// Type for the joi validation events
type JoiEvent = {
    error: Joi.ValidationResult
};

/**
 * Applies validation to a form via Joi.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 * @see https://joi.dev/api
 * @see https://joi.dev/module/joi-date/api
 */
export default class JoiValidation {
    private static _emitter: Emitter<JoiEvent> = mitt<JoiEvent>();

    /**
     * Form to validate.
     */
    private form: HTMLFormElement;

    /**
     * Validation schema.
     */
    private readonly schema: { [key: string]: any };

    /**
     * Joi validation schema.
     */
    private joiSchema: Joi.ObjectSchema;

    static get emitter(): Emitter<JoiEvent> {
        return this._emitter;
    }

    /**
     * Gets custom Joi error messages. Also see JoiValidation.showErrorMessage() for additional, custom error messages.
     *
     * @returns { [key: string]: string } Messages
     */
    private static getJoiMessages(): { [key: string]: string } {
        return {
            'string.base': 'Kein gültiger Text.',
            'string.empty': 'Dieses Feld muss ausgefüllt werden.',
            'string.min': 'Der Text muss mindestens {#limit} Zeichen enthalten.',
            'string.max': 'Der Text darf maximal {#limit} Zeichen enthalten.',
            'string.uri': 'Der angegebene Wert ist keine gültige URL (URL muss mit "http://" oder "https://" beginnen).',
            'string.email': 'Der angegebene Wert ist keine gültige E-Mail Adresse.',
            'number.base': 'Keine gültige Zahl.',
            'number.empty': 'Dieses Feld muss ausgefüllt werden.',
            'number.min': 'Die Zahl muss mindestens {#limit} sein.',
            'number.max': 'Die Zahl darf maximal {#limit} sein.',
            'number.integer': 'Die Zahl muss eine ganzzahlige Zahl sein.',
            'checkbox.checked': 'Die Checkbox muss aktiviert sein.',
            'checkbox.oneChecked': 'Mindestens eine Checkbox muss aktiviert sein.',
            'date.base': 'Kein gültiges Datum. Datum muss im Format TT.MM.JJJJ eingegeben werden. Achten Sie darauf, ein korrektes Datum einzugeben!',
            'date.empty': 'Dieses Feld muss ausgefüllt werden.',
            'date.min': 'Das Datum muss mindestens {#limit} sein.',
            'date.max': 'Das Datum darf maximal {#limit} sein.',
            'date.less': 'Das Datum muss kleiner als {#limit} sein.',
            'date.greater': 'Das Datum muss größer als {#limit} sein.',
            'date.format': 'Das Datum muss im Format TT.MM.JJJJ eingegeben werden. Achten Sie darauf, ein korrektes Datum einzugeben!',
            'any.ref': 'Das Feld ist von "{#ref}" abhängig.',
            'any.only': 'Der Wert stimmt nicht überein.',
            'any.required': 'Dieses Feld muss ausgefüllt werden.',
            'any.notempty': 'Dieses Feld muss ausgefüllt werden.'
        };
    }

    /**
     * Gets a fresh Joi object schema with some default values already configured.
     *
     * @param { [key: string]: any } schema Validation schema
     * @returns {Joi.ObjectSchema}
     */
    public static getJoiSchema(schema: { [key: string]: any }) {
        return ExtendedJoi.object(schema).messages(JoiValidation.getJoiMessages());
    }

    /**
     * Initializes Joi form validation defined by data-attributes, as opposed to a dedicated JavaScript validation definition. To make this work with a form,
     * add `data-validate="1"` to the `<form>`-tag to be validated. Each field that should be validated, also needs to receive the data attribute
     * `data-validate="1"`, followed by additional data attributes that define the Joi validation. See JoiValidation.getJoiSchemaForField() for details on
     * all available validation types.
     */
    public static initializeDataAttributeValidation(): void {
        document.querySelectorAll<HTMLFormElement>('form[data-validate="1"]').forEach(form => {
            const schema: { [key: string]: any } = {};

            form.querySelectorAll<HTMLInputElement>('[data-validate="1"]').forEach(field => {
                const fieldSchema = JoiValidation.getJoiSchemaForField(field);
                const id = field.getAttribute('id');
                if (id) {
                    schema[id] = fieldSchema;
                }
            });

            new JoiValidation(form, schema);
        });
    }

    /**
     * Gets a Joi schema definition for a field. Parses certain data-attributes on the element to choose corresponding Joi methods.
     *
     * @param {HTMLInputElement} field Field to get schema for
     * @returns {Joi.Schema|Joi.Reference|null} Joi schema for this field or null, of no schema could be generated
     */
    public static getJoiSchemaForField(field: HTMLInputElement): Schema | Reference | null {
        if (!field.getAttribute('id')) {
            throw new Error('Field has no ID set.');
        }
        if (!field.dataset.validate || field.dataset.validate !== '1') {
            throw new Error('Field has no data-validate="1" set.');
        }

        // Adds common validators to a date field
        const addDateValidators = (schema: DateSchema) => {
            if (field.dataset.validateLessField) {
                schema = schema.less(ExtendedJoi.ref(field.dataset.validateLessField, {
                    adjust: (value) => {
                        // If the less field is allowed to be empty, return some very large date to pass the validation
                        if (!value && field.dataset.validateLessFieldRequired && field.dataset.validateLessFieldRequired === '0') {
                            return moment('2999-01-01').toDate();
                        }
                        return value;
                    }
                }));
            }
            if (field.dataset.validateGreaterField) {
                schema = schema.greater(ExtendedJoi.ref(field.dataset.validateGreaterField, {
                    adjust: (value) => {
                        // If the greated field is allowed to be empty, return some past date to pass the validation
                        if (!value && field.dataset.validateGreaterFieldRequired && field.dataset.validateGreaterFieldRequired === '0') {
                            return moment('1970-01-01').toDate();
                        }
                        return value;
                    }
                }));
            }
            if (field.dataset.validateLessDate) {
                schema = schema.less(field.dataset.validateLessDate);
            }
            if (field.dataset.validateGreaterDate) {
                schema = schema.greater(field.dataset.validateGreaterDate);
            }
            return schema;
        };

        // Add validators for a field based on the selected type
        const id = field.getAttribute('id');
        let schema;

        switch (field.dataset.validateType) {
            case 'string':
                schema = Joi.string();
                break;
            case 'float':
                schema = Joi.number();
                break;
            case 'integer':
                schema = Joi.number().integer();
                break;
            case 'zip':
                schema = Joi.number().integer().min(1000).max(9999);
                break;
            case 'email':
                schema = Joi.string().email({tlds: {allow: false}});
                break;
            case 'uri':
                schema = Joi.string().uri();
                break;
            case 'checkboxChecked':
                schema = Joi.string().custom((value, helpers) => {
                    if (!field.checked) {
                        return helpers.error('checkbox.checked');
                    }
                    return value;
                });
                break;
            case 'oneCheckboxOfMultipleChecked':
                schema = Joi.string().custom((value, helpers) => {
                    const name = field.getAttribute('name');
                    if (name) {
                        let oneCheckboxChecked = false;
                        document.querySelectorAll<HTMLInputElement>(`[name="${name}"]`).forEach(checkbox => {
                            if (checkbox.checked) {
                                oneCheckboxChecked = true;
                                return;
                            }
                        });
                        if (!oneCheckboxChecked) {
                            return helpers.error('checkbox.oneChecked');
                        }
                    }
                    return value;
                });
                break;
            case 'date':
                schema = ExtendedJoi.date().format('DD.MM.YYYY');
                schema = addDateValidators(schema);
                break;
            case 'dateFrom':
                schema = ExtendedJoi.date().format('DD.MM.YYYY');
                schema = addDateValidators(schema);
                break;
            case 'dateTo':
                schema = ExtendedJoi.date().format('DD.MM.YYYY');
                schema = addDateValidators(schema);
                break;
            case 'password':
                schema = Joi.string().min(4).max(255);
                break;
            case 'passwordRepeat':
                if (!field.dataset.validatePasswordField) {
                    throw new Error(`Field "${id}": Password repeat needs to have data-validate-password`);
                }

                return Joi.ref(field.dataset.validatePasswordField);
            default:
                throw new Error(`Field "${id}": Invalid validation type or type missing.`);
        }

        if (field.dataset.validateMin) {
            const min = parseFloat(field.dataset.validateMin);
            if (isNaN(min)) {
                throw new Error(`Field "${id}": Cannot cast min-value to a number.`);
            }
            schema = schema.min(min);
        }

        if (field.dataset.validateMax) {
            const max = parseFloat(field.dataset.validateMax);
            if (isNaN(max)) {
                throw new Error(`Field "${id}": Cannot cast max-value to a number.`);
            }
            schema = schema.max(max);
        }

        if (field.dataset.validateRequired && field.dataset.validateRequired === '1') {
            schema = schema.required();
        } else {
            schema = schema.allow('', null);
        }

        if (field.dataset.validateNotempty && field.dataset.validateNotempty === '1') {
            schema = Joi.string().custom((value, helpers) => {
                if (!value || value === '' || value === null || value === 0 || value === '0') {
                    return helpers.error('any.notempty');
                }
                return value;
            });
        }

        return schema;
    }

    /**
     * Joi validation constructor.
     *
     * @param {HTMLFormElement} form Form to validate
     * @param { [key: string]: any } schema Validation schema to use for validation; key is a ID-name of a form field, value the Joi schema to apply
     */
    constructor(form: HTMLFormElement, schema: { [key: string]: any }) {
        this.form = form;
        this.schema = schema;
        this.joiSchema = JoiValidation.getJoiSchema(schema);
        this.form.addEventListener('submit', e => {
            this.handleSubmit(e);
        });
    }

    /**
     * Handles submitting of the form. Will stop submitting, if errors are present.
     *
     * @param {SubmitEvent} e Form submit event
     * @returns {boolean} True when there were no errors, otherwise false
     */
    private handleSubmit(e: SubmitEvent): boolean {
        this.resetAllErrorMessages();
        const values = this.getValues();

        const result = this.joiSchema.validate(values, {
            abortEarly: false,
            allowUnknown: true
        });

        if (result.error) {
            // Remember the topmost field with errors and scroll to it after validation
            let topmostFieldOffset = 0;

            for (let i = 0; i < result.error.details.length; i++) {
                const error = result.error.details[i];
                const field = this.form.querySelector('#' + error.path[0]) as HTMLInputElement;

                if (field) {
                    this.showErrorMessage(field, error);
                    const position = GeneralUtility.getElementPosition(field);
                    if (topmostFieldOffset === 0 || position.top < topmostFieldOffset) {
                        topmostFieldOffset = position.top;
                    }
                }
            }

            // Scroll to the first field with an error
            if (topmostFieldOffset > 0) {
                const offset = topmostFieldOffset - 100;
                window.scrollTo({
                    top: offset > 0 ? offset : 0,
                    behavior: 'smooth'
                });
            }

            JoiValidation.emitter.emit('error', result);
            e.preventDefault();
            return false;
        }

        return true;
    }

    /**
     * Gets the values being submitted by the form. This method will try to select elements by their ID-attribute. All keys within `this.schema` will be tried.
     *
     * @returns { [key: string]: any } Form values
     */
    private getValues(): { [key: string]: any } {
        const values: { [key: string]: any } = {};

        for (const key of Object.keys(this.schema)) {
            const field = this.form.querySelector<HTMLInputElement>('#' + key);
            if (field) {
                values[key] = field.value;
            } else {
                values[key] = null;
            }
        }
        return values;
    }

    /**
     * Resets all error messages for a form.
     */
    private resetAllErrorMessages() {
        this.form.querySelectorAll('.invalid-feedback').forEach(invalidFeedback => {
            invalidFeedback.remove();
        });
        this.form.querySelectorAll('.is-invalid').forEach(field => {
            field.classList.remove('is-invalid');
        });
    }

    /**
     * Resets an error message for a field.
     *
     * @param {HTMLInputElement} field Field to reset error message for
     */
    private resetErrorMessage(field: HTMLInputElement) {
        field.classList.remove('is-invalid');
        const invalidFeedback = field.nextElementSibling as HTMLElement;
        if (invalidFeedback.classList.contains('invalid-feedback')) {
            invalidFeedback.remove();
        }
    }

    /**
     * Shows an optional error message for the input element.
     *
     * @param {HTMLInputElement} field Field to show error message for
     * @param {Joi.ValidationErrorItem} error Joi validation error
     */
    private showErrorMessage(field: HTMLInputElement, error: Joi.ValidationErrorItem) {
        // Custom message present as data attribute
        let message = error.message;
        if (field.dataset.validateErrorMessage) {
            message = field.dataset.validateErrorMessage;
        }

        // Special error messages for certain types
        let reference, referencedField, date = null;

        switch (error.type) {
            // Resolve date restriction
            case 'date.less':
            case 'date.greater':
                // Resolve reference and show referenced greater/less date value
                reference = this.resolveReference(message);
                if (reference) {
                    referencedField = this.form.querySelector('#' + reference) as HTMLInputElement;
                    if (referencedField) {
                        message = message.replace('ref:' + reference, referencedField.value);
                    }
                }
                // Resolve an ISO 8601 date and show a pretty date instead
                else {
                    date = this.resolveIso8601Date(message);
                    if (date) {
                        message = message.replace(date.toISOString(), date.format('DD.MM.YYYY'));
                    }
                }
                break;

            // Resolve reference and show name of the other field
            case 'any.ref':
                reference = this.resolveReference(message);
                if (reference) {
                    referencedField = this.form.querySelector<HTMLInputElement>('#' + reference);
                    if (referencedField) {
                        const id = referencedField.getAttribute('id');
                        const label = this.form.querySelector<HTMLElement>('[for="' + reference + '"]');
                        if (label && label.textContent) {
                            message = message.replace('ref:' + reference, label.textContent.trim());
                        } else if (id) {
                            message = message.replace('ref:' + reference, id);
                        }
                    }
                }
                break;
        }

        field.classList.add('is-invalid');
        const invalidFeedback = document.createElement('div');
        invalidFeedback.classList.add('invalid-feedback');
        invalidFeedback.textContent = message;
        field.insertAdjacentElement('afterend', invalidFeedback);
    }

    /**
     * Joi uses annotations like `ref:fieldname` in error messages, to show dynamic values in error messages. This method resolves those annotations and tries
     * to return the referenced field name.
     *
     * @param {string} message Error message
     * @returns {string} Referenced field name
     */
    private resolveReference(message: string): string {
        const regExp = new RegExp(/^.*ref:([\w\-_.]+).*$/, 'i');
        const matches = regExp.exec(message);
        return matches && matches.length === 2 ? matches[1] : '';
    }

    /**
     * Joi returns a value like "2022-09-11T22:00:00.000Z" when using the `validate-greater/less-date` validators. This is an ISO 8601 date, which is not
     * really useful to display to a user. This method parses the ISO 8601 string, and returns a fresh moment.js object, so that a custom error message can
     * be created.
     *
     * @param {string} message Error message
     * @returns {moment.Moment|null} Moment
     */
    private resolveIso8601Date(message: string): moment.Moment | null {
        const regExp = new RegExp(/^.*(\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d.\d\d\dZ).*$/, 'i');
        const matches = regExp.exec(message);

        if (matches && matches[1]) {
            const date = moment(matches[1]);
            if (date.isValid()) {
                return date;
            }
            return null;
        }
        return null;
    }
}
